import React, { useCallback, useEffect, useRef } from 'react'
import { bindActionCreators, Dispatch } from 'redux'
import { connect } from 'react-redux'
import CardCollection, { CardCollectionSkeleton } from 'components/Card/CardCollection'
import { RootState, RootActionType } from 'duck'
import { apiActions, apiSelectors } from 'duck/ApiDuck'
import { FixedSizeList, RenderItemProps } from 'components/ListVirtualized'
import { MineImage } from 'models/ApiModels'
import { actions, selectors } from '../../duck'
import ListLoading, { setResponsiveValue } from 'components/Loading/ListLoading'
import { useBreakpoint } from 'utils/Hooks'
import { useGetElementScroll } from 'components/ElementScroll'
import { GRIDLOADING_CONFIG } from './Constants'

const CONFIG_GRID = {
  xs: {
    columns: 2,
    defaultColumnWidth: 170,
    defaultRowHeight: 170,
    gutter: 6
  },
  sm: {
    columns: 3
  },
  default: {
    columns: 5,
    defaultColumnWidth: 250,
    defaultRowHeight: 200,
    gutter: 8
  }
}

const mapStateToProps = (state: RootState) => ({
  openedMineProject: apiSelectors.openedMineProject(state),
  currentMineProjectCollectedList: apiSelectors.currentMineProjectCollectedList(state),
  collectedListParams: selectors.collectedListParams(state),
  collectedListloaded: selectors.collectedListloaded(state),
  listMineProjectCollectedOutputsLoading:
    apiSelectors.loading['mineProject.listMineProjectCollectedOutputs'](state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      reloadCollected: actions.reloadCollected,
      listMineProjectCollectedOutputs: apiActions.mineProject.listMineProjectCollectedOutputs
    },
    dispatch
  )

type ListCollectedProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>

const ListCollected: React.FC<ListCollectedProps> = props => {
  const {
    collectedListloaded,
    collectedListParams,
    openedMineProject,
    listMineProjectCollectedOutputsLoading,
    currentMineProjectCollectedList,
    listMineProjectCollectedOutputs,
    reloadCollected
  } = props

  const breakpointData = useBreakpoint()
  const projectId = openedMineProject?.id ?? 0
  const collectedListLength = currentMineProjectCollectedList.length
  const collectedOutputsLoadingRef = useRef(listMineProjectCollectedOutputsLoading)
  const network = openedMineProject?.network ?? 'ETHEREUM'
  const loadMoreCollected = useCallback(() => {
    !collectedOutputsLoadingRef.current &&
      listMineProjectCollectedOutputs({ param: collectedListParams, next: true })
  }, [listMineProjectCollectedOutputs, collectedListParams, collectedOutputsLoadingRef])

  const renderItem = useCallback(
    (props: RenderItemProps<MineImage>) => {
      const { item, measure, width } = props

      if (!item || !width) return null

      return <CardCollection network={network} key={item.id} mineImage={item} onLoad={measure} />
    },
    [network]
  )

  const noCollectedItem =
    (listMineProjectCollectedOutputsLoading === false &&
      collectedListloaded &&
      !collectedListLength) ||
    (!openedMineProject?.mine_count && !collectedListLength)

  const allCollectedWorksLoaded = openedMineProject?.mine_count === collectedListLength

  const adjustedItems = setResponsiveValue(breakpointData, GRIDLOADING_CONFIG.items)
  const scrollRef = useGetElementScroll('Page')

  useEffect(() => {
    if (!collectedListloaded && projectId) {
      reloadCollected()
    }
  }, [collectedListloaded, projectId, reloadCollected])

  useEffect(() => {
    collectedOutputsLoadingRef.current = listMineProjectCollectedOutputsLoading
  }, [listMineProjectCollectedOutputsLoading])

  if (noCollectedItem) return null

  if (!collectedListLength && openedMineProject?.mine_count)
    return (
      <ListLoading
        config={CONFIG_GRID}
        component={<CardCollectionSkeleton />}
        items={
          adjustedItems && adjustedItems > openedMineProject?.mine_count
            ? openedMineProject?.mine_count
            : adjustedItems
        }
      />
    )

  if (!collectedListLength) return null

  return (
    <FixedSizeList
      data={currentMineProjectCollectedList}
      config={CONFIG_GRID}
      renderItem={renderItem}
      onEndOfGrid={collectedListloaded && !allCollectedWorksLoaded ? loadMoreCollected : undefined}
      loading={Boolean(listMineProjectCollectedOutputsLoading)}
      scrollRef={scrollRef}
      autoHeight
    />
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(ListCollected)
